import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Hotel, Star, Paperclip, MapPin, Calendar, Users, DollarSign } from "lucide-react";
import { HotelReservation, HotelPricing } from "@/hooks/useTrips";
import { EditHotelDialog } from "../dialogs/EditHotelDialog";
import { DeleteItemDialog } from "../dialogs/DeleteItemDialog";
import { AttachmentsSheet, AttachmentsBadge, SourceEmailBadge } from "../attachments";
import { getCompanyLogoUrl } from "@/lib/companyLogos";
import { safeFormat } from "@/lib/formatDateTime";

interface HotelCardProps {
  hotel: HotelReservation;
  tripId?: string;
  index?: number;
  pricing?: HotelPricing;
  readOnly?: boolean;
}

export const HotelCard = ({ hotel, tripId, index, pricing, readOnly = false }: HotelCardProps) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return "N/A";
    const result = safeFormat(dateStr, "MMM d, yyyy");
    return result === "—" ? dateStr : result;
  };

  const formatPrice = (price: number, currency: string) => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return formatter.format(price);
  };

  // Calculate nights
  const nights = hotel.check_in_date && hotel.check_out_date
    ? Math.ceil((new Date(hotel.check_out_date).getTime() - new Date(hotel.check_in_date).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  // Generate star icons based on hotel.stars
  const renderStars = (stars: number) => {
    return Array.from({ length: stars }, (_, i) => (
      <Star key={i} className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
    ));
  };

  // Get hotel logo URL
  const logoUrl = getCompanyLogoUrl(hotel.hotel_name, 64);

  // Display name - fallback to address/city if no hotel name
  const displayName = hotel.hotel_name || hotel.address || hotel.city || "Hotel";

  // Check if there are expandable details to show
  // Guests names are shown in expanded (above fold only shows count)
  const hasExpandableDetails = !!(
    (hotel.guests && hotel.guests.length > 0)
  );

  return (
    <>
      <Card variant="interactive">
        <CardContent className="p-6">
          {/* Header with hotel info and actions */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
            <div className="flex items-center gap-3">
              {hotel.hotel_image ? (
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                  <img
                    src={hotel.hotel_image}
                    alt={displayName}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : logoUrl ? (
                <div className="w-12 h-12 rounded bg-purple-50 border border-purple-200 flex items-center justify-center overflow-hidden">
                  <img
                    src={logoUrl}
                    alt={displayName}
                    className="w-8 h-8 object-contain mix-blend-multiply"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.innerHTML = `<span class="text-xs font-bold text-purple-600">${displayName.substring(0, 2).toUpperCase()}</span>`;
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="w-12 h-12 rounded bg-purple-50 border border-purple-200 flex items-center justify-center">
                  <span className="text-xs font-bold text-purple-600">{displayName.substring(0, 2).toUpperCase()}</span>
                </div>
              )}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-lg">{displayName}</h3>
                  {hotel.stars && hotel.stars > 0 && (
                    <div className="flex items-center gap-0.5">
                      {renderStars(hotel.stars)}
                    </div>
                  )}
                </div>
                {hotel.address && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {hotel.address}{hotel.city && `, ${hotel.city}`}
                  </p>
                )}
                {/* Rating badge */}
                {hotel.rating !== undefined && (
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary" className="bg-blue-600 text-white hover:bg-blue-700">
                      {hotel.rating.toFixed(1)}
                    </Badge>
                    {hotel.rating_label && (
                      <span className="text-sm text-muted-foreground">{hotel.rating_label}</span>
                    )}
                    {hotel.review_count !== undefined && (
                      <span className="text-sm text-muted-foreground">
                        ({hotel.review_count.toLocaleString()} reviews)
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            {!readOnly && tripId !== undefined && index !== undefined && (
              <div className="flex items-center gap-2">
                <AttachmentsBadge
                  tripId={tripId}
                  itemType="hotels"
                  itemIndex={index}
                  onClick={() => setShowAttachments(true)}
                />
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {hotel.source_email_id && (
                    <SourceEmailBadge onClick={() => setShowAttachments(true)} />
                  )}
                  <Button variant="ghost" size="icon" onClick={() => setShowAttachments(true)} title="Attachments">
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setShowEdit(true)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setShowDelete(true)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Check-in/Check-out - 2 Column Layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Check-in</div>
              <div className="font-semibold">{formatDate(hotel.check_in_date)}</div>
              <div className="text-sm text-muted-foreground">
                {hotel.check_in_time || <span className="italic">After 3:00 PM</span>}
              </div>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Check-out</div>
              <div className="font-semibold">{formatDate(hotel.check_out_date)}</div>
              <div className="text-sm text-muted-foreground">
                {hotel.check_out_time || <span className="italic">Before 11:00 AM</span>}
              </div>
            </div>
          </div>

          {/* Quick Info */}
          <div className="flex flex-wrap gap-4 text-sm mb-4">
            {nights > 0 && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Calendar className="w-4 h-4" />
                {nights} night{nights !== 1 ? 's' : ''}
              </div>
            )}
            {hotel.room_type && (
              <div className="text-muted-foreground">{hotel.room_type}</div>
            )}
            {hotel.guests && hotel.guests.length > 0 && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Users className="w-4 h-4" />
                {hotel.guests.length} guest{hotel.guests.length !== 1 ? 's' : ''}
              </div>
            )}
            {hotel.confirmation_number && (
              <div className="text-muted-foreground">
                <span className="font-medium">Conf:</span> {hotel.confirmation_number}
              </div>
            )}
          </div>

          {/* Pricing Info */}
          {pricing && (
            <div className="bg-gradient-to-r from-accent/10 to-accent/5 rounded-lg p-4 mb-4 border border-accent/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Rate per night:</span>
                <span className="font-semibold">{formatPrice(pricing.retail_price, pricing.currency)}</span>
              </div>
              {nights > 0 && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Nights:</span>
                  <span className="font-semibold">{nights}</span>
                </div>
              )}
              <div className="border-t border-accent/20 pt-2 mt-2 flex items-center justify-between">
                <span className="font-semibold flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  Total Cost:
                </span>
                <span className="text-xl font-bold text-accent">
                  {formatPrice(pricing.retail_price * (nights || 1), pricing.currency)}
                </span>
              </div>
            </div>
          )}

          {/* Expandable Details */}
          {expanded && (
            <div className="border-t border-border pt-4 space-y-3">
              {hotel.guests && hotel.guests.length > 0 && (
                <div>
                  <span className="text-sm font-medium">Guests:</span>
                  <div className="mt-1 space-y-1">
                    {hotel.guests.map((g, i) => (
                      <div key={i} className="text-sm text-muted-foreground ml-2">
                        {g.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {hotel.address && (
                <div>
                  <span className="text-sm font-medium">Full Address:</span>
                  <p className="text-sm text-muted-foreground mt-1">
                    {hotel.address}{hotel.city && `, ${hotel.city}`}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Toggle Button - only show if there are details to expand */}
          {hasExpandableDetails && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-4 text-sm text-accent hover:text-accent/80 transition-colors"
            >
              {expanded ? 'Show less' : 'Show more details'}
            </button>
          )}
        </CardContent>
      </Card>

      {!readOnly && tripId !== undefined && index !== undefined && (
        <>
          <EditHotelDialog
            hotel={hotel}
            tripId={tripId}
            index={index}
            open={showEdit}
            onOpenChange={setShowEdit}
          />
          <DeleteItemDialog
            tripId={tripId}
            itemType="hotels"
            index={index}
            itemName={hotel.hotel_name}
            open={showDelete}
            onOpenChange={setShowDelete}
          />
          <AttachmentsSheet
            open={showAttachments}
            onOpenChange={setShowAttachments}
            tripId={tripId}
            itemType="hotels"
            itemIndex={index}
            itemTitle={hotel.hotel_name}
            sourceEmailId={hotel.source_email_id}
          />
        </>
      )}
    </>
  );
};
